import React, { useState } from 'react';
import { Form, Space, Modal, Upload, Button, Table, message, Typography } from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { apiRequest } from '../../../../service/apiRequest';

const { Text } = Typography;

interface Props {
    visible: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const sampleTemplate = [
    {
        holiday_name: '',
        holiday_status: 1, // 1 = active, 0 = inactive
        holiday_description: '',
        holiday_start_date: '2025-01-01',
        holiday_end_date: '2025-01-01',
        holiday_type: 'Public',
    },
];

const ImportHolidayModal: React.FC<Props> = ({ visible, onClose, onSuccess }) => {
    const [parsedData, setParsedData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const downloadSample = () => {
        const ws = XLSX.utils.json_to_sheet(sampleTemplate);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'HolidayTemplate');
        XLSX.writeFile(wb, 'holiday_sample_template.xlsx');
    };

    const handleUpload = async (file: File) => {
        setLoading(true);
        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(sheet, { defval: '' });

            if (!json.length) {
                message.warning('The Excel file is empty.');
                return false;
            }

            setParsedData(json);
        } catch (err) {
            console.error(err);
            message.error('Failed to read Excel file. Please check the format.');
        } finally {
            setLoading(false);
        }

        return false; // prevent auto-upload
    };

    const handleImport = async () => {
        try {
            await apiRequest({
                method: 'post',
                url: '/import-holidays',
                data: parsedData,
            });
            message.success('Holidays imported successfully!');
            onSuccess();
            onClose();
        } catch (err) {
            console.error(err);
            message.error('Import failed. Check the data format.');
        }
    };

    return (
        <Modal
            title="Import Holidays"
            width="75%"
            open={visible}
            onCancel={onClose}
            onOk={handleImport}
            okText="Import"
            okButtonProps={{ disabled: parsedData.length === 0 || loading, loading }}
            destroyOnHidden
        >
            <Form layout="vertical">
                <Form.Item label="Step 1">
                    <Space direction="vertical" size={4}>
                        <Text type="secondary">Download and fill in the sample template</Text>
                        <Button icon={<DownloadOutlined />} onClick={downloadSample}>
                            Download Sample Template
                        </Button>
                    </Space>
                </Form.Item>

                <Form.Item label="Step 2">
                    <Space direction="vertical" size={4}>
                        <Text type="secondary">Upload the filled template</Text>
                        <Upload
                            accept=".xlsx"
                            beforeUpload={handleUpload}
                            showUploadList={false}
                            disabled={loading}
                        >
                            <Button icon={<UploadOutlined />} loading={loading}>
                                Upload Excel File
                            </Button>
                        </Upload>
                    </Space>
                </Form.Item>
            </Form>

            {parsedData.length > 0 && (
                <>
                    <Text style={{ marginTop: 12, display: 'block' }}>Preview of Imported Data:</Text>
                    <Table
                        dataSource={parsedData}
                        rowKey={(_, index) => index!.toString()}
                        size="small"
                        scroll={{ y: 200 }}
                        pagination={false}
                        columns={Object.keys(sampleTemplate[0]).map((key) => ({
                            title: key,
                            dataIndex: key,
                            key,
                        }))}
                        style={{ marginTop: 8 }}
                    />
                </>
            )}
        </Modal>
    );
};

export default ImportHolidayModal;
